import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { createNewLead } from "@/lib/lead/newLeads";
import type { ExtractedLeadV2, Lead } from "../../types/lead";
import type { Location } from "../../types/location";

type TranscriptCandidate = { transcript: string };

type UseLeadBulkTranscriptParams = {
  extract: (transcript: string) => Promise<ExtractedLeadV2>;
  extractBulk: (bulkTranscript: string) => Promise<TranscriptCandidate[]>;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  currentLeadRef: MutableRefObject<Lead | null>;
  setTranscript: Dispatch<SetStateAction<string>>;
  transcriptRef: MutableRefObject<string>;
  location: Location | null;
};

export function useLeadBulkTranscript(params: UseLeadBulkTranscriptParams) {
  const splitBulkTranscriptFallback = useCallback((bulkTranscript: string): TranscriptCandidate[] => {
    const raw = bulkTranscript.trim();
    if (!raw) return [];
    const minChunk = 12;
    const asChunks = (parts: string[]) =>
      parts
        .map((s) => s.trim().replace(/\s+/g, " "))
        .filter((s) => s.length >= minChunk)
        .map((transcript) => ({ transcript }));

    const byLines = asChunks(raw.split(/\n+/));
    if (byLines.length > 1) return byLines;

    const byParagraph = asChunks(raw.split(/\n\s*\n+/));
    if (byParagraph.length > 1) return byParagraph;

    const topicBreak = /\b(?:next customer|another (?:customer|lead|stop)|also (?:met|spoke with|had)|---+|•)\b/gi;
    const breakMatches = [...raw.matchAll(topicBreak)];
    if (breakMatches.length > 0) {
      const idxs = [0, ...breakMatches.map((m) => (m.index ?? 0) + m[0].length), raw.length];
      const slices: string[] = [];
      for (let i = 0; i < idxs.length - 1; i++) {
        const seg = raw.slice(idxs[i], idxs[i + 1]).trim();
        if (seg.length >= minChunk) slices.push(seg);
      }
      const topicChunks = asChunks(slices);
      if (topicChunks.length > 1) return topicChunks;
    }

    const sentences = raw.split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/);
    const clusters: string[] = [];
    let buf = "";
    for (const s of sentences) {
      const part = s.trim();
      if (!part) continue;
      const merged = `${buf} ${part}`.trim();
      if (merged.length < 45) buf = merged;
      else {
        if (buf.length >= minChunk) clusters.push(buf);
        buf = part;
      }
    }
    if (buf.length >= minChunk) clusters.push(buf);
    const sentenceChunks = asChunks(clusters);
    if (sentenceChunks.length > 1) return sentenceChunks;

    const markerPattern = /([A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\s+(?:is|was)\b)/g;
    const markers = Array.from(raw.matchAll(markerPattern));
    if (markers.length <= 1) {
      if (byLines.length) return byLines;
      if (sentenceChunks.length) return sentenceChunks;
      return raw.length >= minChunk ? [{ transcript: raw }] : [];
    }
    const chunks: TranscriptCandidate[] = [];
    for (let i = 0; i < markers.length; i++) {
      const start = markers[i]?.index ?? 0;
      const end = i + 1 < markers.length ? markers[i + 1]?.index ?? raw.length : raw.length;
      const segment = raw.slice(start, end).trim();
      if (segment.length >= minChunk) chunks.push({ transcript: segment });
    }
    return chunks.length > 1 ? chunks : byLines.length ? byLines : sentenceChunks;
  }, []);

  const createLeadsFromBulkTranscript = useCallback(
    async (bulkTranscript: string) => {
      const candidates = await params.extractBulk(bulkTranscript);
      const fallbackCandidates = candidates.length <= 1 ? splitBulkTranscriptFallback(bulkTranscript) : [];
      const leadCandidates =
        candidates.length > 1 ? candidates : fallbackCandidates.length > 1 ? fallbackCandidates : candidates;
      if (!leadCandidates.length) {
        return { count: 0, names: [] as string[] };
      }
      const created: Lead[] = [];
      for (const candidate of leadCandidates) {
        const lead = createNewLead({
          originLocationId: params.location?.id ?? null,
          originLocationName: params.location?.name ?? null,
        });
        const extractedCandidate = await params.extract(candidate.transcript);
        created.push({
          ...lead,
          transcript: candidate.transcript,
          extracted: extractedCandidate,
          name:
            [extractedCandidate.first_name, extractedCandidate.last_name].filter(Boolean).join(" ").trim() ||
            candidate.transcript.split(/[,.;\n]/)[0]?.trim().slice(0, 48) ||
            lead.name,
          lastUpdated: Date.now(),
          needs_review: true,
          follow_ups: [],
        });
      }
      params.setLeads((prev) => [...created, ...prev]);
      params.setCurrentLead(created[0] ?? null);
      params.currentLeadRef.current = created[0] ?? null;
      params.setTranscript(created[0]?.transcript ?? "");
      params.transcriptRef.current = created[0]?.transcript ?? "";
      return { count: created.length, names: created.map((l) => l.name) };
    },
    [params, splitBulkTranscriptFallback]
  );

  return { createLeadsFromBulkTranscript };
}
